import { useState, useEffect, useMemo, memo } from "react";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { useLocation } from '@reach/router'
import { FixedSizeList as List, areEqual } from "react-window";
import { ReactComponent as IconPrinter } from 'bootstrap-icons/icons/printer.svg'
import uniqBy from 'lodash/uniqBy'

import { formatDate } from "../../../config/i18n";
import useStore from "../useStore";
import useQuery from "hooks/useQuery";
import useUser from "hooks/useUser";
import useSocket from "hooks/useSocket";
import Event, { COMPACT_ROW_HEIGHT } from "./Event/Event";
import Print from "./Print";
import Calendar from "../Sidebar/Calendar";
import styles from "./styles.module.scss";

// Fix responsive (2026-08-14): las columnas de ancho fijo de Event.js
// (125px hora + 320px paciente + 170px financiador) suman más que
// cualquier celular en portrait — por debajo de este ancho de CONTENEDOR
// (no de viewport: si el sidebar de escritorio está abierto y angosta la
// agenda, también aplica), Event.js pasa a columnas flexibles.
const COMPACT_BREAKPOINT = 768

export const sort = (a, b) => {
  if (a.status === "arrived" && b.status !== "arrived") return -1;
  if (a.status !== "arrived" && b.status === "arrived") return 1;
  if (a.status === "active" && b.status !== "active") return -1;
  if (a.status !== "active" && b.status === "active") return 1;
  if (
    ["fulfilled", "cancelled", "noshow"].includes(a.status) &&
    !["fulfilled", "cancelled", "noshow"].includes(b.status)
  )
    return 1;
  if (
    !["fulfilled", "cancelled", "noshow"].includes(a.status) &&
    ["fulfilled", "cancelled", "noshow"].includes(b.status)
  )
    return -1;
  const aDatetime = `${a.day}T${a.time}`
  const bDatetime = `${b.day}T${b.time}`
  if (aDatetime < bDatetime) return -1;
  if (aDatetime > bDatetime) return 1;
  return 0
};

const Loader = () => {
  const { t } = useTranslation();
  return (
    <div
      className={`d-flex flex-column align-items-center justify-content-center p-5`}
    >
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">
          {t("loading", { context: "slot" })}
        </span>
      </div>
    </div>
  );
};

const Row = memo((props) => {
  const { index, style, data } = props;

  return (
    <div style={style}>
      <Event event={data.events[index]} compact={data.compact} />
    </div>
  );
}, areEqual);

const Dropdown = ({ date }) => {
  return (
    <div className="dropdown">
      <button data-bs-toggle="dropdown" className="text-first-up dropdown-toggle btn btn-lg text-start p-0 flex-fill text-truncate">
        {formatDate(date, "eeee d - MMMM yyyy")}
      </button>
      {/* Fix responsive (2026-08-14): 280px fijo en las 3 propiedades no
          dejaba a Popper achicar el menú si el viewport es más angosto que
          eso más los márgenes — min() cubre el mismo ancho en desktop y cede
          en un celular chico (320-375px). */}
      <div
        className="dropdown-menu p-0"
        style={{
          width: 'min(280px, calc(100vw - 24px))',
          maxWidth: 'min(280px, calc(100vw - 24px))',
          minWidth: 'min(280px, calc(100vw - 24px))',
        }}
      >
        <Calendar />
      </div>
    </div>
  )
}

function Agenda(props) {
  const { t } = useTranslation();
  const store = useStore(state => ({
    location: state.location?.id,
    resource: state.resource?.id,
    update: state.update,
  }));
  const user = useUser();
  const [dimensions, setDimensions] = useState({});
  // Hallazgo 2026-07-27: un useRef + efecto dependiendo de `$container.current`
  // NO se re-ejecuta cuando el ref se adjunta — React no trackea mutaciones de
  // ref como dependencia de efecto, así que ese efecto corría UNA sola vez al
  // montar, con `.current` todavía en `null` si el contenedor no existía ese
  // primer render (ej. detrás de un loader condicional). El ResizeObserver
  // nunca llegaba a engancharse, dimensions quedaba en {} para siempre, y la
  // agenda mostraba vacío pese a que query.data sí tenía turnos reales (mismo
  // síntoma que el hallazgo 2026-07-17 de abajo, causa distinta). Un callback
  // ref (vía useState) SÍ dispara re-render cuando el nodo se adjunta.
  const [$container, setContainerNode] = useState(null);
  const socket = useSocket()
  const [events, setEvents] = useState({})
  const [data, setData] = useState([])
  const location = useLocation()

  const [status, setStatus] = useState();

  const date = new Date(`${props.date}T00:00`)
  const filters = {}

  filters.events = {
    cancelled: true,
    noshow: true,
    type: "appointment",
    startsAt: date.getTime(),
    endsAt: date.getTime() + (24 * 60 * 60 * 1000 - 1),
    resource: store.resource,
    location: store.location,
    // service: store.service,
    // financier: store.financier,
  };

  const query = useQuery(window._l(`/events/query`).substr(1), {
    body: filters.events,
    initialData: []
  });

  filters.availability = {
    startsAt: new Date().setHours(0, 0, 0, 0),
    endsAt: new Date().setHours(23, 59, 59, 999),
    resource: store.resource,
    location: store.location,
    free: false
  };

  const availability = useQuery(window._l(`/availability`).substr(1), {
    body: filters.availability,
  });

  const keys = [
    "all",
    "booked",
    "arrived",
    "active",
    "fulfilled",
    "noshow",
    "cancelled",
    "pending",
  ];



  // Hallazgo 2026-07-17: este efecto medía offsetHeight/offsetWidth UNA vez
  // (dependiendo de JSON.stringify(dimensions), que se "traba" en cuanto el
  // resultado se repite — dos objetos {height:0,width:0,ready:false}
  // stringifican IGUAL, así que React nunca vuelve a correr el efecto) — si
  // el contenedor medía 0 en el instante exacto de ese primer render (el
  // layout del flex padre todavía no había resuelto su altura), la agenda
  // quedaba mostrando "No hay turnos" PARA SIEMPRE aunque query.data sí
  // tuviera turnos reales (confirmado: los contadores de arriba mostraban
  // "all 1"/"booked 1" con la lista igual vacía). El segundo efecto (abajo,
  // ahora eliminado) intentaba paliarlo con un listener de window "resize",
  // pero nunca se dispara si el contenedor cambia de tamaño por layout
  // (flex/CSS) sin que la VENTANA cambie — exactamente lo que pasa acá.
  // Un ResizeObserver real sobre el contenedor reacciona a cualquier cambio
  // de tamaño (layout o ventana) sin depender de un estado que pueda
  // "trabarse", y no necesita el segundo efecto en absoluto.
  useEffect(() => {
    if (!$container) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const { width, height } = entry.contentRect
      setDimensions((prev) => (
        prev.width === width && prev.height === height ? prev : { width, height }
      ))
    })
    observer.observe($container)
    return () => observer.disconnect()
  }, [$container]);

  useEffect(() => {
    const { facets } = availability.data || {}
    store.update({ facets })
  }, [availability.data]);

  useEffect(() => {
    const callback = (data) => {
      if (location.pathname.includes(data.teamID)) {
        events[data.id] = data
        setEvents(JSON.clone(events))
      }
    }
    if (socket) socket.on('events', callback)
    return () => socket?.off('events', callback)
  }, [socket, props.date, events, location.pathname])

  useEffect(() => {
    let data = {}
    if (Array.isArray(query.data)) {
      for (const event of query.data) {
        data[event.id] = { ...event }
      }
      for (const event of Object.values(events)) {
        data[event.id] = { ...event }
      }

      // Bug real (auditoría 2026-08-25, reportado por Juan Cruz QA): la
      // query al backend ya filtra type:'appointment', pero los eventos que
      // llegan por socket (ej. crear un bloqueo de día) se mergean acá sin
      // ese filtro — un evento type:'lock' (sin consumer/financier/service/
      // resource, ver Form.js) llegaba a Event.js/Simple.js, que asumen esos
      // campos siempre presentes, y crasheaba toda la agenda al seleccionar
      // esa fecha. Los bloqueos ya se muestran en la vista de Calendario
      // (Slot.js), que sí los maneja explícitamente — acá nunca debieron
      // listarse como turnos.
      data = Object.values(data)
        .filter((e) => {
          let eventDate;
          if (e.startsAt || e.datetime) {
            eventDate = new Date(e.startsAt || e.datetime).toLocaleDateString('fr-ca');
          } else if (e.day) {
            eventDate = e.day;
          } else {
            eventDate = new Date(Date.now()).toLocaleDateString('fr-ca');
          }
          return (
            eventDate === props.date &&
            e.type !== 'lock' &&
            (!status || e.status === status) &&
            (!store.location || e.location?.id === store.location) &&
            (!store.resource || e.resource?.id === store.resource)
          );
        })
        .sort(sort);

      setData(uniqBy(data, 'id'))
    }
  }, [events, query.data, props.date, status, store.location, store.resource])

  const compact = dimensions.width > 0 && dimensions.width < COMPACT_BREAKPOINT
  // react-window compara `data` por referencia (areEqual) para decidir si
  // re-renderiza cada fila — memoizado para no romper esa memoización en
  // cada render del padre (solo cambia si cambian los turnos o el modo compact).
  const rowData = useMemo(() => ({ events: data, compact }), [data, compact])

  // Bug real (auditoría 2026-08-14): cada badge de la barra de tabs
  // (8 keys: all/booked/arrived/active/fulfilled/noshow/cancelled/pending)
  // recorría `query.data` completo recalculando `eventDate` para cada
  // evento — 8 pasadas completas del dataset en CADA render de Agenda, sin
  // memoizar. Una sola pasada agrupa los conteos por status (nota: a
  // propósito NO filtra por store.location/store.resource, igual que el
  // .filter inline que reemplaza — esos filtros sólo aplican a `data`, la
  // lista efectivamente renderizada, no a los conteos de los tabs).
  const eventCountsByStatus = useMemo(() => {
    const counts = { all: 0 }
    if (!Array.isArray(query.data)) return counts
    for (const event of query.data) {
      let eventDate;
      if (event.startsAt || event.datetime) {
        eventDate = new Date(event.startsAt || event.datetime).toLocaleDateString('fr-ca');
      } else if (event.day) {
        eventDate = event.day;
      } else {
        eventDate = new Date(Date.now()).toLocaleDateString('fr-ca');
      }
      if (eventDate !== props.date) continue
      counts.all += 1
      counts[event.status] = (counts[event.status] || 0) + 1
    }
    return counts
  }, [query.data, props.date])

  if (!user.state.uid) return <Loader />;

  // Bug real (auditoría 2026-08-14): sin esto, un error real de la query
  // (caído de red, 500, etc.) dejaba `query.data` en undefined para
  // siempre — indistinguible, desde acá, de "todavía está cargando". El
  // profesional se quedaba mirando el spinner sin ningún indicio de que
  // la agenda no iba a cargar sola.
  if (query.isError) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center p-5 gap-2">
        <div className="alert alert-danger small mb-0">
          {t('agenda_load_error', 'No pudimos cargar la agenda — revisá tu conexión.')}
        </div>
        <button className="btn btn-sm btn-link" onClick={() => query.refetch()}>
          {t('retry', 'Reintentar')}
        </button>
      </div>
    );
  }

  if (!Array.isArray(query.data)) return <Loader />;

  return (
    <div className="mvh-100-1" data-testid="agenda">
      <div className="mb-1 ms-1">
        {/* Bug real (auditoría 2026-08-21): vw-100 (100% del VIEWPORT) en vez
            de w-100 (100% del contenedor) — con el ms-1 del padre ya
            corriendo la caja 4px a la derecha, el borde derecho terminaba
            4px más allá del viewport real: un desborde horizontal chico
            pero real en cualquier ancho. */}
        <div className="bg-white w-100 d-lg-flex align-items-lg-center shadow-sm rounded px-2 px-lg-3 py-2">
          <div className="d-flex align-items-center">
            {query.data.length > 0 && (
              <div className="me-2">
                <button onClick={() => window.print()} className="btn btn-sm bg-light text-secondary" aria-label={t('print_agenda', 'Imprimir agenda')} data-tooltip-id="app-tooltip" data-tooltip-content={t('print_agenda', 'Imprimir agenda')}>
                  <IconPrinter width="16" height="16" />
                </button>
              </div>
            )}
            <Dropdown date={props.date} />
          </div>

          {query.data.length > 0 && (
            <div className="overflow-auto d-flex flex-nowrap align-items-center gap-3 ps-2">
              {keys.map((key) => (
                <button
                  key={`tab-${key}`}
                  className={cx("btn btn-sm text-nowrap", {
                    "link-primary": key === "all" ? !status : status === key,
                    "text-muted": key === "all" ? !!status : status !== key,
                  })}
                  onClick={() => setStatus(key === "all" ? undefined : key)}
                >
                  {t(key, { context: "agenda" })}
                  <sup className="ms-1" data-testid={`tab-count-${key}`}>
                    {eventCountsByStatus[key] || 0}
                  </sup>
                </button>
              ))}
            </div>
          )}

        </div>
      </div>

      <div className={`overflow bg-white-auto ms-1 ${styles.container}`} ref={setContainerNode}>
        {query.isFetching ? (
          <div className="mvh-100-1 d-flex flex-column justify-content-center align-items-center">
            <Loader />
          </div>
        ) : query.data.length > 0 && dimensions.height > 0 && dimensions.width > 0 ? (
          <List
            height={dimensions.height}
            itemCount={data.length}
            itemData={rowData}
            itemSize={compact ? COMPACT_ROW_HEIGHT : 100}
            width={dimensions.width}
          >
            {Row}
          </List>
        ) : (
          <div className="mvh-100-1 d-flex flex-column justify-content-center align-items-center">
            <div className="p-3 alert alert-warning mx-1">
              <h6 className="m-0">{t("no_events", 'No hay turnos', { context: "agenda" })}</h6>
            </div>
          </div>
        )}
      </div>
      {/* Bug real: query.data?.length && data.length con longitud 0 en
          cualquiera de los dos colapsa la expresión al NÚMERO 0 (no a
          false/undefined) — React lo renderiza como texto literal "0"
          suelto en pantalla. !! fuerza un booleano real. */}
      {!!query.data?.length && !!data.length && <Print events={data} date={formatDate(props.date, "eeee d - MMMM yyyy")} />}
    </div>
  );
}

export default Agenda;
